import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useEffect, useRef, useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { openPositionsBunja } from '@flipster/r230522/original/position';
import { Badge } from '@flipster/react/design-system/badge';
import { css, cx } from '@flipster/styled-system/css';
import { flex, grid, stack } from '@flipster/styled-system/patterns';
import { scrollbar } from '@flipster/styled-system/recipes';

import { positionCountBunja } from '@web/hooks/trade/use-close-positions';

import PositionItem from './PositionItem';

const gap = 8;
const minW = 166;

const PositionTab = () => {
  const headerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { openPositionsAtom } = useBunja(openPositionsBunja);
  const positionCountAtom = useBunja(positionCountBunja);
  const openPositions = useAtomValue(openPositionsAtom);
  const { openCount } = useAtomValue(positionCountAtom);
  const [isWindowOverflow, setIsWindowOverflow] = useState(false);

  const openPositionList = Object.values(openPositions || {}).flatMap((positions) => positions);

  useEffect(() => {
    if (!containerRef.current) return;

    const handler = () => {
      const containerWidth = containerRef.current?.offsetWidth || 0;
      const breakPoint = minW * 2 + gap;
      setIsWindowOverflow(
        containerWidth <= breakPoint || openCount === 0
          ? false
          : openCount * minW + gap * (openCount - 1) <= containerWidth,
      );
    };
    handler();
    const ro = new ResizeObserver(() => {
      handler();
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [openCount]);

  return (
    <div className={stack({ h: '100%' })}>
      <div ref={headerRef} className={flex({ gap: 4, py: 12, px: 16 })}>
        <h1 className={css({ textStyle: 'Text/L/Strong' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Trades' />
        </h1>
        <Badge color='neutral'>
          <FormattedMessage
            id='Autogen_common.r251126.s_openCount_positions'
            values={{
              openCount: openCount,
            }}
          />
        </Badge>
      </div>
      <div className={cx(css({ h: '100%', px: 16, pb: 16, overflowY: 'scroll' }), scrollbar())}>
        {openPositionList.length ? (
          <div
            ref={containerRef}
            className={grid({
              gridTemplateColumns: isWindowOverflow
                ? `repeat(auto-fill, ${minW}px)`
                : `repeat(auto-fit, minmax(${minW}px, 1fr))`,
              gridAutoRows: '1fr',
              gap,
            })}
          >
            {openPositionList.map((position) => (
              <PositionItem key={position.positionId} position={position} />
            ))}
          </div>
        ) : (
          <div
            className={css({
              textStyle: 'Text/M/Regular',
              p: '8px 16px',
              bgColor: 'background.default.level-3',
              color: 'content.default.level-5',
              rounded: 8,
            })}
          >
            <FormattedMessage id='Autogen_common.r251126.s_All_effective_contracts_you_possess_show_here' />
          </div>
        )}
      </div>
    </div>
  );
};

export default PositionTab;
